import React from 'react';
import { useSelector } from 'react-redux';
import { DataCard, Select } from '@brighterion/br-ui';
import { SORT_VIEW } from 'Common/enums';
import { useTranslation, Trans } from 'react-i18next';
import { getUser, shouldShowMerchants } from 'Selectors';
import useGetLatestRepositoryConfig from 'Hooks/query-hooks/repository/useGetLatestRepositoryConfig';
import { DateService } from 'Services';
import { getCustomUserName } from 'Utils';
import useReplaceMerchant from 'Hooks/useReplaceMerchant';
import clsx from 'clsx';
import styles from '../RulePerformanceReportView.css';

const sortViewSelect = (
    sortView: SortType,
    setSortView: (value: SortType) => void,
    t: (text: string, options?: any) => string,
    size?: 'sm' | 'lg'
) => (
    <Select
        className={styles.sortViewSelectContainer}
        labelClassName={styles.sortViewSelectLabel}
        label={t('Sort')}
        handleClickCallback={(value) => setSortView(value[0])}
        placeholder='-Select-'
        selectedItems={[sortView]}
        inlineLabel
        size={size}
    >
        <option value={SORT_VIEW.ALPHANUMERICAL}>{t(SORT_VIEW.ALPHANUMERICAL)}</option>
        <option value={SORT_VIEW.HIGH}>{t(SORT_VIEW.HIGH)}</option>
        <option value={SORT_VIEW.LOW}>{t(SORT_VIEW.LOW)}</option>
    </Select>
);

type Props = {
    formValues: ProcessedPerformanceReportFormValues;
    sortView: SortType;
    setSortView: (value: SortType) => void;
    createdReportInfo?: CreatedReportInfo;
    newlyGenerated?: boolean;
    totalTransactions: any;
    users?: UsersHashmap;
};

const HeaderRows = ({
    formValues,
    sortView,
    setSortView,
    createdReportInfo,
    newlyGenerated,
    totalTransactions,
    users
}: Props) => {
    const { t } = useTranslation();
    const { userId, username } = useSelector(getUser);
    const { dateRangeLabel, dateRange, merchant, currencyEffectiveDate: existingCurrencyEffectiveDate } = formValues;
    const { dateRangeValue, dateRangeType } = dateRange;
    const replacedMerchant = useReplaceMerchant(merchant);
    const { data: repositoryData } = useGetLatestRepositoryConfig();
    const currencyEffectiveDate = DateService.getDateTime(
        existingCurrencyEffectiveDate || repositoryData?.currencyEffectiveDate
    );

    const renderCreatedBy = () => {
        // if the current user is found from users_info table in DB
        // display username from DB, otherwise, display username from mcconect / okta
        if (newlyGenerated) return `${getCustomUserName(userId, users, username)} (${t('me')})`;

        // If the current user is not the creator of the current report and is found from users_info table in DB
        // display username from DB, else display username from the report endpoint
        const reportCreator = createdReportInfo && getCustomUserName(createdReportInfo.createdBy, users);
        return userId === createdReportInfo?.createdBy ? `${reportCreator} (${t('me')})` : `${reportCreator}`;
    };

    const reportHeader = (
        <>
            <Trans i18nKey='Transactions evalulated'>
                WTF <strong>{{ totalTransactions }}</strong> transactions evaluated. Percent rounded to the nearest
                100th.
            </Trans>
        </>
    );

    return (
        <>
            <div className={styles.inlineHeaderRowWrapper}>
                <div className={clsx(styles.flexRow, styles.inlineReportInfoWrapper)}>
                    {shouldShowMerchants() && (
                        <DataCard
                            dataTestId='report-merchantType-card'
                            className={clsx(styles.reportCard)}
                            key='merchantType'
                            data={replacedMerchant}
                            label={t('Merchant')}
                        />
                    )}
                    <DataCard
                        dataTestId='report-reportType-card'
                        className={clsx(styles.reportCard)}
                        key='reportType'
                        data={t('Rule Performance')}
                        label={t('Report Type')}
                    />
                    <DataCard
                        dataTestId='report-dateRange-card'
                        className={clsx(styles.reportCard)}
                        key='dateRangeType'
                        data={dateRangeType}
                        label={t('Date Range')}
                    />
                    <DataCard
                        dataTestId='report-dates-card'
                        className={clsx(styles.reportCard)}
                        key='dates'
                        data={DateService.getReportDateRange(dateRangeType, dateRangeValue, dateRangeLabel)}
                        label={t('Dates')}
                    />
                    <DataCard
                        dataTestId='report-createdAt-card'
                        className={clsx(styles.reportCard)}
                        key='created'
                        data={newlyGenerated ? DateService.getDateTime(`${new Date()}`) : createdReportInfo?.createdAt}
                        label={newlyGenerated ? t('Generated') : t('Created')}
                    />
                    <DataCard
                        dataTestId='report-createdBy-card'
                        className={clsx(styles.reportCard)}
                        key='createdBy'
                        data={renderCreatedBy()}
                        label={newlyGenerated ? t('Generated By') : t('Created By')}
                    />
                    <DataCard
                        dataTestId='currency-effective-date-card'
                        className={clsx(styles.reportCard)}
                        key='currencyEffectiveDate'
                        data={currencyEffectiveDate}
                        label={t('Currency Effective Date')}
                    />
                </div>
            </div>
            <div className={styles.inlineHeaderRowWrapper}>
                <span className={styles.roundedMsg}>{reportHeader}</span>
                <span className={styles.sortViewSelectContainer}>{sortViewSelect(sortView, setSortView, t, 'sm')}</span>
            </div>
        </>
    );
};

export default HeaderRows;
